<div class="dashboard-page">
  <!-- ── Notice + book closure month selector ──────────────── -->
  <div class="dashboard-notice">
    <span class="notice-label">
      The dashboard currently displays the book closure data for
    </span>
    <select
      class="month-select"
      [(ngModel)]="selectedBookClosureDetail"
      [compareWith]="compareBookClosure"
      (ngModelChange)="onBookClosureChange()"
      >
      @for (item of bookClosureDetails; track item) {
        <option [ngValue]="item">
          {{ item.month }}
        </option>
      }
    </select>
  </div>

  <!-- ── Row 1: Stats panels ─────────────────────────────── -->
  <div class="dashboard-container">
    @for (panel of panels; track panel; let i = $index) {
      <div class="dashboard-panel">
        <div class="panel-header">
          <div class="panel-title">
            <svg
              class="panel-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              >
              <path [attr.d]="panel.iconPath"></path>
            </svg>
            <span class="panel-title-text">{{ panel.title }}</span>
          </div>
        </div>
        @if (!loadingPanels[i]) {
          <div class="panel-body">
            @for (stat of panel.stats; track stat) {
              <div class="stat-item">
                <div class="stat-label">
                  <span class="dot" [ngClass]="stat.dotClass"></span>
                  {{ stat.label }}
                </div>
                <div class="stat-value" [ngClass]="stat.colorClass">
                  {{ formatValue(stat.value) }}
                </div>
              </div>
            }
          </div>
        }
        @if (loadingPanels[i]) {
          <div class="panel-body skeleton-body">
            @for (stat of panel.stats; track stat) {
              <div class="skeleton-stat">
                <div class="skeleton-line skeleton-sm"></div>
                <div class="skeleton-line skeleton-lg"></div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>

  <!-- ── Row 2: Allocation Overview panels ──────────────── -->
  <div class="dashboard-container">
    @for (panel of panels; track panel; let i = $index) {
      <div class="overview-card">
        @if (!loadingAllocationOverviews[i] && allocationOverviews[i]; as ov) {
          <div class="overview-header">
            <span class="overview-title"
              >{{ ov.centerLabel }} Overview</span
              >@if (i === 2) {
              <span class="overview-subtitle"> (Last 6-Month Trend) </span>
            }
          </div>
          <!-- Donut chart for Component and Final Assembly -->
          @if (i < 2) {
            <div class="donut-wrapper">
              <svg viewBox="0 0 100 100" class="donut-svg">
                <g transform="rotate(-90 50 50)">
                  @for (seg of ov.segments; track seg) {
                    <circle
                      cx="50"
                      cy="50"
                      r="35"
                      fill="none"
                      [attr.stroke]="seg.color"
                      stroke-width="14"
                      [attr.stroke-dasharray]="seg.dasharray"
                      [attr.stroke-dashoffset]="seg.dashoffset"
                      >
                      <title>
                        {{ seg.label }}:
                        {{ formatValue(seg.value) }}
                      </title>
                    </circle>
                  }
                </g>
                <!-- Center labels -->
                <text
                  x="50"
                  y="43"
                  text-anchor="middle"
                  class="donut-text-top"
                  >
                  Total
                </text>
                <text
                  x="50"
                  y="52"
                  text-anchor="middle"
                  class="donut-text-mid"
                  >
                  {{ ov.centerLabel }}
                </text>
                <text
                  x="50"
                  y="71"
                  text-anchor="middle"
                  class="donut-text-val"
                  >
                  {{ formatValue(ov.total) }}
                </text>
              </svg>
            </div>
          }
          <!-- Stacked bar chart for Simulation monthly data -->
          @if (i === 2) {
            <div class="bar-chart-wrapper">
              @if (simulationBarEntries.length > 0) {
                <svg
                  [attr.viewBox]="simulationBarViewBox"
                  class="bar-chart-svg"
                  preserveAspectRatio="xMidYMid meet"
                  >
                  @for (entry of simulationBarEntries; track entry; let bi = $index) {
                    <g>
                      @for (seg of entry.segments; track seg) {
                        <rect
                          [attr.x]="bi * 50 + 11"
                          [attr.y]="seg.y"
                          width="28"
                          [attr.height]="seg.height"
                          [attr.fill]="seg.color"
                          rx="2"
                          >
                          <title>{{ seg.label }}: {{ formatValue(seg.value) }}</title>
                        </rect>
                      }
                      <!-- total value above each bar -->
                      <text
                        [attr.x]="bi * 50 + 25"
                        [attr.y]="+entry.topY - 3"
                        text-anchor="middle"
                        class="bar-value"
                      >{{ formatValue(entry.total) }}</text>
                      <text
                        [attr.x]="bi * 50 + 25"
                        y="128"
                        text-anchor="middle"
                        class="bar-label"
                      >{{ entry.label }}</text>
                    </g>
                  }
                </svg>
                <!-- legend -->
                <div class="bar-legend">
                  <span class="bar-legend-item">
                    <span class="bar-legend-dot" style="background:#5b9bd5"></span>SEU
                  </span>
                  <span class="bar-legend-item">
                    <span class="bar-legend-dot" style="background:#6bba8a"></span>SLA
                  </span>
                  <span class="bar-legend-item">
                    <span class="bar-legend-dot" style="background:#e57373"></span>Errors
                  </span>
                </div>
              } @else {
                <span class="bar-no-data">No monthly data</span>
              }
            </div>
          }
        }
        @if (loadingAllocationOverviews[i]) {
          <div class="overview-header">
            <span class="overview-title"
              >{{ panel.title }} Overview</span
              >
            </div>
            <div class="donut-wrapper skeleton-donut-wrapper">
              <div class="skeleton-donut"></div>
            </div>
          }
        </div>
      }
    </div>

    <!-- ── Row 3: Allocation by Unit ──────────────────────── -->
    <div class="dashboard-container">
      @for (i of [0, 1]; track i) {
        @if (!loadingAllocationByUnits[i] && allocationByUnits[i]; as section) {
          <div class="unit-card">
            <div class="unit-card-header">
              <span class="unit-card-title">{{ section.title }}</span>
            </div>
            <table class="unit-table">
              <thead>
                <tr>
                  <th class="col-unit">Unit</th>
                  <th class="col-num">Produced</th>
                  <th class="col-num">Allocated</th>
                  <th class="col-num">Flow Errors</th>
                  <th class="col-status">Status</th>
                  <th class="col-arrow"></th>
                </tr>
              </thead>
              <tbody>
                @for (row of section.rows; track row) {
                  <tr class="unit-row">
                    <td class="col-unit">
                      <div class="unit-name-cell">
                        <span class="unit-icon-wrap">
                          <svg
                            class="unit-row-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            >
                            <path
                              [attr.d]="row.iconPath"
                            ></path>
                          </svg>
                        </span>
                        <span class="unit-name">{{
                          row.name
                        }}</span>
                      </div>
                    </td>
                    <td class="col-num stat-orange">
                      {{ formatValue(row.produce) }}
                    </td>
                    <td class="col-num stat-green">
                      {{ formatValue(row.allocated) }}
                    </td>
                    <td class="col-num stat-red">
                      {{ formatValue(row.errors) }}
                    </td>
                    <td class="col-status">
                      <div class="status-cell">
                        <span
                          class="dot"
                                        [ngClass]="
                                            row.status === 'Allocated'
                                                ? 'dot-green'
                                                : 'dot-red'
                                        "
                        ></span>
                        <span class="status-text">{{
                          row.status
                        }}</span>
                      </div>
                    </td>
                    <td
                      class="col-arrow"
                      (click)="navigateToAllocation(section, row)"
                      >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        >
                        <polyline
                          points="9 18 15 12 9 6"
                        ></polyline>
                      </svg>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
        @if (loadingAllocationByUnits[i]) {
          <div class="unit-card">
            <div class="unit-card-header">
              <div class="skeleton-line skeleton-title"></div>
            </div>
            <div class="skeleton-table">
              @for (r of [1, 2, 3, 4]; track r) {
                <div class="skeleton-row">
                  <div class="skeleton-line skeleton-cell"></div>
                  <div class="skeleton-line skeleton-cell-sm"></div>
                  <div class="skeleton-line skeleton-cell-sm"></div>
                  <div class="skeleton-line skeleton-cell-sm"></div>
                </div>
              }
            </div>
          </div>
        }
      }

      <!-- Simulation Details -->
      @if (loadingSimDetails || (missingPartMarket1 && missingPartMarket1.length > 0)) {
        <div
          class="unit-card"
          >
          <div class="unit-card-header">
            <div>
              <span class="unit-card-title">Missing Simulation Material Prices</span>
            </div>
          </div>
          @if (loadingSimDetails) {
            <div class="skeleton-table">
              @for (r of [1, 2]; track r) {
                <div class="skeleton-row">
                  <div class="skeleton-line skeleton-cell"></div>
                  <div class="skeleton-line skeleton-cell-sm"></div>
                </div>
              }
            </div>
          }
          @if (!loadingSimDetails && missingPartMarket1 && missingPartMarket1.length > 0) {
            <table class="unit-table">
              <thead>
                <tr>
                  <th class="col-unit">Market</th>
                  <th class="col-num">Total Missing Count</th>
                </tr>
              </thead>
              <tbody>
                @for (row of missingPartMarket1; track row) {
                  <tr class="unit-row">
                    <td class="col-unit">
                      <div class="unit-name-cell">
                        <span class="unit-name">{{
                          row.marketName
                        }}</span>
                      </div>
                    </td>
                    <td class="col-num stat-green">
                      {{ formatValue(row.totalCount) }}
                    </td>
                    <td
                      class="col-arrow"
                      (click)="navigateToSimMissingMaterial(row)"
                      >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      }
    </div>
  </div>
